/* ═══════════════════════════════════════════════════════════
   PRODUCT-CAROUSEL.JS — PALS
   Carrusel horizontal de colección con filtros por tipo.

   Responsabilidades:
   1. Medir la geometría REAL del .container y publicar --pcar-edge
      y --pcar-item. El CSS solo puede aproximarla con 100vw (que
      incluye la barra de scroll y descuadra la última tarjeta).
   2. Filtrar por tipo (energía / proteína / bits) sin vaciar nunca
      el carrusel: si un filtro no tiene productos, el chip queda
      atenuado y no es clickeable.
   3. Flechas, barra de progreso y teclado.

   Patrón del theme: IIFE → guard global → init/cleanup/reinit →
   listeners nombrados → hooks shopify:section:* bajo designMode.
   Debug: ?pcardebug en la URL.
   ═══════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  if (window.__pcar) {
    window.__pcar.reinit();
    return;
  }

  var DEBUG = /[?&]pcardebug/.test(location.search);
  var instances = [];

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[pcar]'].concat([].slice.call(arguments)));
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function setup(root) {
    var viewport = root.querySelector('[data-pcar-viewport]');
    var track = root.querySelector('[data-pcar-track]');
    if (!viewport || !track) return null;

    var head = root.querySelector('[data-pcar-measure]');
    var prev = root.querySelector('[data-pcar-prev]');
    var next = root.querySelector('[data-pcar-next]');
    var fill = root.querySelector('[data-pcar-progress-fill]');
    var chips = [].slice.call(root.querySelectorAll('[data-pcar-filter]'));
    var items = [].slice.call(root.querySelectorAll('[data-pcar-item]'));
    var animated = root.classList.contains('pcar--anim') && !reducedMotion();

    /* ── 1 · Geometría ───────────────────────────────────────
       El ancho del ítem se deriva del ancho INTERNO del .container
       (medido, no calculado con 100vw) para que N tarjetas encajen
       exactas en el ancho de contenido y la sangría lateral coincida
       con la del resto de la página. */
    function measure() {
      if (!head) return;
      var cs = getComputedStyle(head);
      var inner = head.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0);
      if (!(inner > 0)) return;

      var rootW = root.clientWidth;
      var edge = Math.max(0, (rootW - inner) / 2);

      // --pcar-n-active lo resuelven los media queries del CSS.
      var n = parseFloat(getComputedStyle(root).getPropertyValue('--pcar-n-active')) || 1.2;
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 20;

      var item = (inner - gap * (n - 1)) / n;
      if (!(item > 0)) return;

      root.style.setProperty('--pcar-edge', edge + 'px');
      root.style.setProperty('--pcar-item', item + 'px');
      // Para el modo "respetar ambos márgenes": el ancho interno del
      // .container y su propio padding, que es distinto del centrado (--edge).
      root.style.setProperty('--pcar-inner', inner + 'px');
      root.style.setProperty('--pcar-pad', parseFloat(cs.paddingLeft || 0) + 'px');
      log('measure', { inner: inner, edge: edge, n: n, gap: gap, item: item });
    }

    /* ── 2 · Estado de scroll: progreso + flechas ─────────── */
    function maxScroll() {
      return Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    }

    function syncScrollState() {
      var max = maxScroll();
      var x = viewport.scrollLeft;
      var scrollable = max > 1;

      root.classList.toggle('is-static', !scrollable);

      if (fill) {
        // Proporción visible como piso: la barra nunca queda en 0.
        var visibleRatio = viewport.clientWidth / Math.max(1, viewport.scrollWidth);
        var progress = scrollable ? x / max : 1;
        var w = (visibleRatio + (1 - visibleRatio) * progress) * 100;
        fill.style.width = Math.min(100, Math.max(6, w)) + '%';
      }

      if (prev) prev.disabled = !scrollable || x <= 1;
      if (next) next.disabled = !scrollable || x >= max - 1;
    }

    /* ── 3 · Paso de las flechas ──────────────────────────
       Avanza tantas tarjetas COMPLETAS como quepan en el viewport
       (mínimo una), para que ningún producto se salte sin verse. */
    function step() {
      var first = items.filter(function (i) { return !i.hidden; })[0];
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 20;
      var itemW = first ? first.getBoundingClientRect().width : viewport.clientWidth;
      var unit = itemW + gap;
      var perView = Math.max(1, Math.floor((viewport.clientWidth + gap) / unit));
      return unit * perView;
    }

    function scrollBy(dir) {
      viewport.scrollBy({
        left: dir * step(),
        behavior: animated ? 'smooth' : 'auto'
      });
    }

    function onPrev() { scrollBy(-1); }
    function onNext() { scrollBy(1); }

    function onScroll() {
      if (onScroll._t) return;
      onScroll._t = requestAnimationFrame(function () {
        onScroll._t = null;
        syncScrollState();
      });
    }

    function onKey(e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); scrollBy(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); scrollBy(-1); }
    }

    /* ── 4 · Filtros por tipo ─────────────────────────────── */
    function countFor(value) {
      if (value === 'all') return items.length;
      var n = 0;
      items.forEach(function (i) {
        if ((i.getAttribute('data-line') || '') === value) n++;
      });
      return n;
    }

    function applyFilter(value) {
      items.forEach(function (i) {
        var match = value === 'all' || (i.getAttribute('data-line') || '') === value;
        i.hidden = !match;
      });
      chips.forEach(function (c) {
        var active = c.getAttribute('data-pcar-filter') === value;
        c.classList.toggle('is-active', active);
        c.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      viewport.scrollTo({ left: 0, behavior: animated ? 'smooth' : 'auto' });
      // El layout se reacomoda en el mismo frame; el estado se lee después.
      requestAnimationFrame(syncScrollState);
    }

    // Chips sin productos: atenuados y sin click (el carrusel nunca se vacía).
    chips.forEach(function (c) {
      var v = c.getAttribute('data-pcar-filter');
      if (v !== 'all' && countFor(v) === 0) {
        c.classList.add('is-empty');
        c.setAttribute('aria-disabled', 'true');
      }
    });

    function onChip(e) {
      var chip = e.currentTarget;
      if (chip.classList.contains('is-empty')) return;
      applyFilter(chip.getAttribute('data-pcar-filter'));
    }

    /* ── 5 · Suscripciones ────────────────────────────────── */
    if (prev) prev.addEventListener('click', onPrev);
    if (next) next.addEventListener('click', onNext);
    viewport.addEventListener('scroll', onScroll, { passive: true });
    viewport.addEventListener('keydown', onKey);
    chips.forEach(function (c) { c.addEventListener('click', onChip); });

    var ro = null;
    if ('ResizeObserver' in window) {
      ro = new ResizeObserver(function () {
        measure();
        syncScrollState();
      });
      ro.observe(root);
    }

    var onResize = null;
    if (!ro) {
      onResize = function () { measure(); syncScrollState(); };
      window.addEventListener('resize', onResize, { passive: true });
    }

    measure();
    syncScrollState();
    // Segunda pasada tras el layout de fuentes/imágenes lazy.
    requestAnimationFrame(function () { measure(); syncScrollState(); });

    return function cleanup() {
      if (prev) prev.removeEventListener('click', onPrev);
      if (next) next.removeEventListener('click', onNext);
      viewport.removeEventListener('scroll', onScroll);
      viewport.removeEventListener('keydown', onKey);
      chips.forEach(function (c) { c.removeEventListener('click', onChip); });
      if (ro) ro.disconnect();
      if (onResize) window.removeEventListener('resize', onResize);
      if (onScroll._t) { cancelAnimationFrame(onScroll._t); onScroll._t = null; }
      root.style.removeProperty('--pcar-edge');
      root.style.removeProperty('--pcar-item');
    };
  }

  /* Las instancias se guardan junto a su root para poder desmontar UNA sola:
     en el customizer, quitar un carrusel disparaba un cleanup global y dejaba
     al otro carrusel de la página sin listeners hasta recargar. */
  function init() {
    [].slice.call(document.querySelectorAll('[data-pcar]')).forEach(function (root) {
      if (instances.some(function (i) { return i.root === root; })) return;
      var teardown = setup(root);
      if (teardown) instances.push({ root: root, teardown: teardown });
    });
    log('init', instances.length, 'carrusel(es)');
  }

  function cleanup(scope) {
    instances = instances.filter(function (i) {
      if (scope && !scope.contains(i.root)) return true;
      i.teardown();
      return false;
    });
  }

  function reinit() {
    cleanup();
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor: re-render de la sección → re-init limpio.
     Gated por designMode: inerte en producción. */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-pcar]')) {
        cleanup(e.target);
        init();
      }
    });
    document.addEventListener('shopify:section:unload', function (e) {
      if (e.target && e.target.querySelector && e.target.querySelector('[data-pcar]')) cleanup(e.target);
    });
  }

  window.__pcar = { reinit: reinit, cleanup: cleanup };
})();
